import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function ConfirmDeleteShiftTypeModal({ open, shiftType, onClose, onDeleted }) {
  const { t } = useTranslation('shiftTypes');
  const [impact, setImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !shiftType) return;
    setImpact(null);
    setDeleting(false);
    setLoadingImpact(true);
    apiClient.get(`/api/shift-templates/${shiftType.id}/delete-impact`)
      .then(res => setImpact(res.data))
      .catch(() => setImpact({ employeesUsingAsDefault: 0 }))
      .finally(() => setLoadingImpact(false));
  }, [open, shiftType]);

  const handleDelete = async () => {
    if (!shiftType) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/shift-templates/${shiftType.id}`);
      onDeleted();
      onClose();
    } catch {
      setDeleting(false);
    }
  };

  if (!open || !shiftType) return null;

  const label = shiftType.label || shiftType.name || shiftType.shiftName || '';
  const count = impact?.employeesUsingAsDefault ?? 0;
  const hasImpact = count > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-5">
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            {t('delete.title', { label })}
          </h2>

          {loadingImpact ? (
            <div className="flex items-center justify-center py-4">
              <Spinner />
            </div>
          ) : (
            <p className="text-sm text-gray-600 leading-relaxed">
              {hasImpact
                ? t('delete.descriptionWithImpact', { count })
                : t('delete.descriptionNoImpact')}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {t('delete.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting || loadingImpact}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting && <Spinner />}
            {hasImpact ? t('delete.confirmWithImpact') : t('delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDeleteShiftTypeModal;